// Inlined into index.html's <head> at build time (vite-plugins/inlineAssetRecovery.ts), so it must stay import-free.
export const ASSET_RELOAD_KEY = "idme:asset-reload";
export const RECOVERING_MESSAGE = "IDME_RECOVERING";
/** Outlasts the pod overlap of a rollout plus Cloudflare's 30s cached 404. */
export const RELOAD_DELAYS_MS = [2000, 10000, 30000, 60000];
export const FAILED_MESSAGE = "Something went wrong loading this page. Please refresh to try again.";

type BootWindow = Window & { __idmeBooted?: boolean };

/** Mid-rollout a new hashed file can 404 from an old pod: reload (behind a spinner) until a matching version loads. */
export function installAssetRecovery(win: BootWindow): void {
  const doc = win.document;
  let scheduled = false;

  win.addEventListener(
    "error",
    (event) => {
      const el = event.target as HTMLScriptElement | HTMLLinkElement | null;
      if (!el || (el.tagName !== "SCRIPT" && !(el.tagName === "LINK" && (el as HTMLLinkElement).rel === "stylesheet"))) return;
      const url = (el as HTMLScriptElement).src || (el as HTMLLinkElement).href;
      if (!url || url.indexOf("/assets/") === -1) return;

      if (el.tagName === "LINK") {
        retryStylesheet(win, el as HTMLLinkElement, url);
        return;
      }
      // After boot, lazy-chunk failures belong to each flow's own error path.
      if (win.__idmeBooted || scheduled) return;

      let attempt: number;
      try {
        attempt = Number(win.sessionStorage.getItem(ASSET_RELOAD_KEY) || 0);
        win.sessionStorage.setItem(ASSET_RELOAD_KEY, String(attempt + 1));
      } catch {
        attempt = RELOAD_DELAYS_MS.length;
      }
      const root = doc.getElementById("root");
      if (attempt >= RELOAD_DELAYS_MS.length) {
        if (root) root.textContent = FAILED_MESSAGE;
        return;
      }
      scheduled = true;
      if (win.parent !== win) win.parent.postMessage({ type: RECOVERING_MESSAGE }, "*");
      if (root && !root.childElementCount) root.innerHTML = '<div class="idme-boot-spinner" role="status" aria-label="Loading"></div>';
      win.setTimeout(() => {
        if (!win.__idmeBooted) win.location.reload();
      }, RELOAD_DELAYS_MS[attempt]);
    },
    true,
  );
}

/** A fresh query each time skips Cloudflare's cached 404 without reloading the page. */
function retryStylesheet(win: Window, el: HTMLLinkElement, url: string) {
  const tries = Number(el.getAttribute("data-retry") || 0);
  if (tries >= RELOAD_DELAYS_MS.length) return;
  win.setTimeout(() => {
    const retry = el.cloneNode() as HTMLLinkElement;
    const base = url.replace(/[?&]retry=\d+$/, "");
    retry.setAttribute("data-retry", String(tries + 1));
    retry.href = `${base}${base.indexOf("?") === -1 ? "?" : "&"}retry=${Date.now()}`;
    el.parentNode?.insertBefore(retry, el.nextSibling);
  }, RELOAD_DELAYS_MS[tries]);
}

/** Called once the app has booted: stops the recovery reloads, and lets a later visit start the schedule over. */
export function markBooted(win: BootWindow): void {
  win.__idmeBooted = true;
  try {
    win.sessionStorage.removeItem(ASSET_RELOAD_KEY);
  } catch {
    // Storage unavailable: recovery never reloads, so there's nothing to clear.
  }
}
